import React from "react";
import { Search, X } from "lucide-react";
import { useSearch } from "@pineseed/gui/hook/useSearch";
import { useEffect, useRef } from "react";

/**
 * SearchBar Component
 * STRICT RULE: Pure UI only - no business logic, no external system calls
 * Renders search input with autocomplete dropdown
 */

interface SearchBarProps {
  className?: string;
  placeholder?: string;
}

export function SearchBar({
  className = "",
  placeholder = "Search...",
}: SearchBarProps) {
  const { searchState, startSearch, stopSearch, search } = useSearch();
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when search is opened
  useEffect(() => {
    if (searchState.isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchState.isOpen]);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    search(query);
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (!searchState.isOpen) {
      startSearch();
    }
  };

  // Handle clear button
  const handleClear = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
      search("");
    }
  };

  // Handle escape key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      stopSearch();
      inputRef.current?.blur();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        {/* Search Icon */}
        {/* <Search
          size={16}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none"
        /> */}

        {/* Search Input */}
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-1 bg-background border border-border rounded-md text-sm placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
        />

        {/* Clear Button */}
        {searchState.query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            title="Clear search"
          >
            <X size={16} />
          </button>
        )}

        {/* Loading Indicator */}
        {searchState.isSearching && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-500 border-t-transparent"></div>
          </div>
        )}
      </div>
    </div>
  );
}
